import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { CreateHuntInput, huntService, Waypoint } from '../services/huntService';
import ImageUpload from '../components/ImageUpload';
import MapPreview from '../components/MapPreview';

const HuntForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateHuntInput>({
    title: '',
    description: '',
    category: 'restaurant',
    difficulty: 'easy',
    estimated_duration: 60,
    location: {
      latitude: 0,
      longitude: 0,
      address: '',
    },
    waypoints: [],
  });

  useEffect(() => {
    if (id) {
      loadHunt(id);
    }
  }, [id]);

  const loadHunt = async (huntId: string) => {
    try {
      setLoading(true);
      const hunt = await huntService.getHunt(huntId);
      setFormData({
        title: hunt.title,
        description: hunt.description,
        category: hunt.category || '',
        difficulty: hunt.difficulty,
        estimated_duration: hunt.estimatedDuration,
        location: hunt.centerPoint ? {
          latitude: hunt.centerPoint.latitude,
          longitude: hunt.centerPoint.longitude,
          address: ''
        } : { latitude: 0, longitude: 0, address: '' },
        waypoints: hunt.locations?.map((loc, idx) => ({
          waypoint_id: loc.id,
          name: loc.locationData?.name || `Location ${idx + 1}`,
          description: loc.locationData?.description || '',
          latitude: loc.locationData?.coordinates.latitude || 0,
          longitude: loc.locationData?.coordinates.longitude || 0,
          order: loc.orderIndex
        })) || [],
      });
    } catch (error) {
      console.error('Error loading hunt:', error);
      alert('Failed to load hunt');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      if (id) {
        await huntService.updateHunt(id, formData);
      } else {
        await huntService.createHunt(formData);
      }
      navigate('/hunts');
    } catch (error) {
      console.error('Error saving hunt:', error);
      alert(`Failed to ${id ? 'update' : 'create'} hunt`);
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLocationChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      location: { ...prev.location, [field]: value },
    }));
  };

  const handleAddWaypoint = () => {
    const newWaypoint: Waypoint = {
      name: '',
      description: '',
      latitude: 0,
      longitude: 0,
      order: formData.waypoints.length + 1,
    };
    setFormData((prev) => ({
      ...prev,
      waypoints: [...prev.waypoints, newWaypoint],
    }));
  };

  const handleWaypointChange = (index: number, field: string, value: any) => {
    const updatedWaypoints = [...formData.waypoints];
    updatedWaypoints[index] = { ...updatedWaypoints[index], [field]: value };
    setFormData((prev) => ({ ...prev, waypoints: updatedWaypoints }));
  };

  const handleRemoveWaypoint = (index: number) => {
    const updatedWaypoints = formData.waypoints.filter((_, i) => i !== index);
    // Reorder remaining waypoints
    updatedWaypoints.forEach((wp, i) => {
      wp.order = i + 1;
    });
    setFormData((prev) => ({ ...prev, waypoints: updatedWaypoints }));
  };

  if (loading && id) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/hunts')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          {id ? 'Edit Hunt' : 'Create New Hunt'}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={(e) => handleChange('category', e.target.value)}
                  >
                    <MenuItem value="restaurant">Restaurant</MenuItem>
                    <MenuItem value="cafe">Cafe</MenuItem>
                    <MenuItem value="bar">Bar</MenuItem>
                    <MenuItem value="street_food">Street Food</MenuItem>
                    <MenuItem value="bakery">Bakery</MenuItem>
                    <MenuItem value="dessert">Dessert</MenuItem>
                    <MenuItem value="pizza">Pizza</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Difficulty</InputLabel>
                  <Select
                    value={formData.difficulty}
                    label="Difficulty"
                    onChange={(e) => handleChange('difficulty', e.target.value)}
                  >
                    <MenuItem value="easy">Easy</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="hard">Hard</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="number"
                  label="Estimated Duration (minutes)"
                  value={formData.estimated_duration}
                  onChange={(e) => handleChange('estimated_duration', parseInt(e.target.value))}
                  required
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Hunt Image
            </Typography>
            <ImageUpload
              onUploadComplete={(url) => handleChange('image_url', url)}
              currentImage={formData.image_url}
              label="Main Hunt Image"
            />
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Location
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.location.address}
                  onChange={(e) => handleLocationChange('address', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Latitude"
                  value={formData.location.latitude}
                  onChange={(e) => handleLocationChange('latitude', parseFloat(e.target.value))}
                  inputProps={{ step: 'any' }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Longitude"
                  value={formData.location.longitude}
                  onChange={(e) => handleLocationChange('longitude', parseFloat(e.target.value))}
                  inputProps={{ step: 'any' }}
                  required
                />
              </Grid>
            </Grid>
            
            {formData.location.latitude !== 0 && formData.location.longitude !== 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Map Preview
                </Typography>
                <MapPreview
                  location={formData.location}
                  waypoints={formData.waypoints}
                  height={300}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Waypoints</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddWaypoint}
              >
                Add Waypoint
              </Button>
            </Box>

            {formData.waypoints.length === 0 ? (
              <Typography color="textSecondary" align="center">
                No waypoints added yet. Click "Add Waypoint" to create one.
              </Typography>
            ) : (
              formData.waypoints.map((waypoint, index) => (
                <Box key={index} sx={{ mb: 3 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1">Waypoint {index + 1}</Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemoveWaypoint(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        value={waypoint.name}
                        onChange={(e) => handleWaypointChange(index, 'name', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={waypoint.description}
                        onChange={(e) => handleWaypointChange(index, 'description', e.target.value)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Latitude"
                        value={waypoint.latitude}
                        onChange={(e) => handleWaypointChange(index, 'latitude', parseFloat(e.target.value))}
                        inputProps={{ step: 'any' }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Longitude"
                        value={waypoint.longitude}
                        onChange={(e) => handleWaypointChange(index, 'longitude', parseFloat(e.target.value))}
                        inputProps={{ step: 'any' }}
                        required
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))
            )}
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={() => navigate('/hunts')}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (id ? 'Update Hunt' : 'Create Hunt')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default HuntForm;
